import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import CustomLayout from "../../../Core/Layout/CustomLayout";
import TickIcon from "../../../Common/Svgs/TickIcon";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Button from "../../../Core/Components/CustomButton";
import getStepDetails from "./getStepDetails";
import { assetType, powerSources } from "./constants";
import EndProcessModal from "./EndProcessModal";
import { createSession, editSession } from "../../../Redux/APIs/sessionsAPI";
import useMotorName from "./useMotorName";
import { updateSession } from "../../../Redux/Slices/sessions.slice";
import ToastMessageModal from "../../PowerAfrica/ToastMessageModal";
import { updateAsset } from "../../../Redux/Slices/powerAfrica.slice";
import getSessionTextType from "../getSessionTextType";
import { MOTOR_TYPE } from "../constants";

const Step = ({ number, title, selected = false, showLine = false }) => {
  return (
    <div className="flex items-center gap-x-2">
      <div
        className="flex items-center justify-center w-[30px] h-[30px] rounded-full"
        style={{
          backgroundColor: selected ? "#085054" : "#DFDFDF",
        }}
      >
        {selected ? (
          <TickIcon size={14} className="text-yellow" />
        ) : (
          <p className="font-aileron text-10 leading-3 font-semibold text-dark-gray">
            {number}
          </p>
        )}
      </div>

      <p
        className={`font-aileron text-10 leading-3 font-semibold text-dark-gray ${
          selected ? "text-primary" : "text-dark-gray"
        }`}
      >
        {title}
      </p>

      {showLine && (
        <div
          className="w-[111px] h-[1px]"
          style={{
            backgroundColor: selected ? "#085054" : "#DFDFDF",
          }}
        />
      )}
    </div>
  );
};

const StepDescription = ({ handleCreate, handleCancel, motorTypeId = 0 }) => {
  const { id } = useParams();

  const { createSessionStep } = useSelector((state) => state.sessions);

  const { title, description } = getStepDetails(createSessionStep);

  const getButtonText = () => {
    // editing
    if (id) {
      if (createSessionStep === 3) return "Save Session";
      if (createSessionStep === 2 && +motorTypeId === MOTOR_TYPE.PETROL)
        return "Save Session";
      return "Next";
    }

    if (+motorTypeId === MOTOR_TYPE.PETROL) return "Create Session";
    if (createSessionStep === 3) return "Create Session";
    return "Next";
  };

  return (
    <div className="p-6 bg-background-white border-y border-light-grey flex items-center justify-between">
      <div className="flex items-center gap-x-2">
        <div className="flex items-center justify-center w-[30px] h-[30px] rounded-full bg-light-grey">
          <p className="font-aileron text-10 leading-3 font-semibold text-dark-gray">
            {createSessionStep}
          </p>
        </div>

        <div>
          <p className="font-poppins text-base font-medium capitalize">
            {title}
          </p>

          <p className="mt-1 text-10 leading-3 font-aileron font-normal text-dark-gray">
            {description}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-x-3">
        <Button
          size="filter"
          width="generateUser"
          colors="grey"
          onClick={handleCancel}
        >
          {createSessionStep === 1 ? "Cancel" : "Previous"}
        </Button>

        <Button
          size="filter"
          width="generateUser"
          type="submit"
          onClick={handleCreate}
        >
          {getButtonText()}
        </Button>
      </div>
    </div>
  );
};

const CreateSessionScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const {
    currentSession,
    createSessionStep,
    showToastMessage,
    sessionThroughUSSD,
  } = useSelector((state) => state.sessions);

  const { currentRequest } = useSelector((state) => state.requests);

  const [endProcessModal, setEndProcessModal] = useState(false);
  const [sessionBatteries, setSessionBatteries] = useState(() => {
    if (id && currentSession?.batteries?.length) {
      return currentSession.batteries.map((item) => item.id);
    }
    return [];
  });
  const [completedSession, setCompletedSession] = useState(false);

  useEffect(() => {
    if (id && currentSession?.batteries?.length && sessionBatteries.length === 0) {
      setSessionBatteries(currentSession.batteries.map((item) => item.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentSession?.id]);

  // find motor name, asset type name while editing a session
  const localMotorName = useMotorName(currentSession?.motorId);
  const localAssetTypeName = currentSession?.assetType
    ? assetType.find((item) => item.id === +currentSession.assetType)?.value
    : "";

  // form state on main component. every step will use this form state (passed as props)
  const { control, formState, handleSubmit, setValue, clearErrors, watch } =
    useForm({
      defaultValues: {
        farmerId: id || sessionThroughUSSD ? currentSession?.farmerId : null,
        farmerName:
          id || sessionThroughUSSD
            ? `${currentSession?.farmerFirstName} ${currentSession?.farmerLastName}`
            : "",
        farmLocationId: id ? `${currentSession?.farmerLocationId}` : "",
        farmLocationName: id
          ? `Farm 1 (${currentSession?.farmLat}, ${currentSession?.farmLong})`
          : "",
        farmLat: null,
        farmLong: null,
        area: id ? currentSession?.area : null,
        estimatedCostPerSqM: id ? currentSession?.estimatedCostPerSqm : null,
        totalCost: id ? currentSession?.totalCost : null,
        activityId:
          id || sessionThroughUSSD ? currentSession?.activityType : null,
        activityName:
          id || sessionThroughUSSD
            ? getSessionTextType(currentSession?.activityType)
            : "",
        activityDate:
          id || sessionThroughUSSD ? currentSession.activityDate : null,
        startTime: id || sessionThroughUSSD ? currentSession?.timeSlot : "",
        hubId: id || sessionThroughUSSD ? currentSession?.hubId : null,
        hubName: id || sessionThroughUSSD ? currentSession?.hubName : "",
        agentId: id ? currentSession?.agentId : null,
        agentName: id
          ? `${currentSession?.agentFirstName} ${currentSession?.agentLastName}`
          : "",
        assetTypeId: id ? currentSession?.assetType : assetType[0].id,
        assetTypeName: id ? localAssetTypeName : assetType[0].value,
        assetId: id ? currentSession?.assetId : null,
        assetRegistrationNo: id ? currentSession?.assetRegistrationNo : "",
        powerSourceId: powerSources[0].id,
        powerSourceName: powerSources[0].value,
        motorTypeId: id ? currentSession?.motorId : null,
        motorTypeName: id ? localMotorName : "",
        estimatedDuration: id
          ? Number(currentSession?.estimatedDuration || 0)
          : 0,
      },
    });

  // watching the motor type
  // if petrol -> no selection of batteries
  // else -> selection of batteries
  const localMotorTypeId = watch("motorTypeId");

  const handleCancel = () => {
    // first step has cancel CTA
    if (createSessionStep === 1) {
      setEndProcessModal(true);
      return;
    }

    // other steps has previous CTA
    // setCurrentStep((prev) => prev - 1);
    dispatch(
      updateSession({
        key: "createSessionStep",
        value: createSessionStep - 1,
      }),
    );
  };

  const handleCreate = async (e) => {
    // final step has create session CTA
    if (
      createSessionStep === 3 ||
      (createSessionStep === 2 && +localMotorTypeId === MOTOR_TYPE.PETROL)
    ) {
      const payload = e;

      // petrol motor will not have any batteries
      payload.batteries =
        +localMotorTypeId === MOTOR_TYPE.PETROL ? [] : sessionBatteries;

      if (id) {
        payload.sessionId = id;
        await dispatch(editSession(payload));

        dispatch(
          updateSession({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "1 Session edited",
                description: `Session ${id} was edited successfully`,
              },
            },
          }),
        );
      } else {
        // if creating and accepting a ussd request
        if (sessionThroughUSSD) {
          payload.ussdId = currentRequest?.id;
        }

        // completed session key -> whether to add an ongoing session or a completed session
        payload.completedSession = completedSession;

        await dispatch(createSession(payload));

        dispatch(
          updateSession({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "1 Session created",
                description: "1 Session was created successfully",
              },
            },
          }),
        );

        dispatch(
          updateSession({
            key: "createSessionStep",
            value: 1,
          }),
        );

        dispatch(
          updateSession({
            key: "sessionThroughUSSD",
            value: false,
          }),
        );
      }

      navigate("/sessions");
      return;
    }

    // other steps has next CTA
    // setCurrentStep((prev) => prev + 1);
    dispatch(
      updateSession({
        key: "createSessionStep",
        value: createSessionStep + 1,
      }),
    );
  };

  const renderSteps = () => {
    switch (createSessionStep) {
      case 1:
        return (
          <Step1
            control={control}
            formState={formState}
            setValue={setValue}
            clearErrors={clearErrors}
            watch={watch}
            completedSession={completedSession}
            setCompletedSession={setCompletedSession}
            setSessionBatteries={setSessionBatteries}
          />
        );

      case 2:
        return (
          <Step2
            control={control}
            formState={formState}
            setValue={setValue}
            clearErrors={clearErrors}
            watch={watch}
          />
        );

      case 3:
        return (
          <Step3
            watch={watch}
            sessionBatteries={sessionBatteries}
            setSessionBatteries={setSessionBatteries}
          />
        );

      default:
        return (
          <Step1
            control={control}
            formState={formState}
            setValue={setValue}
            clearErrors={clearErrors}
            watch={watch}
            setSessionBatteries={setSessionBatteries}
          />
        );
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (showToastMessage.visible) {
        dispatch(
          updateSession({
            key: "showToastMessage",
            value: { visible: false, data: {} },
          }),
        );
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [dispatch, showToastMessage.visible]);

  return (
    <CustomLayout pageName="Create A Session" hideFooter>
      <div className="px-4 py-3 bg-background-white flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <Step
            showLine
            number={1}
            title="Schedule Session"
            selected={createSessionStep > 1}
          />

          <Step
            showLine={+localMotorTypeId !== MOTOR_TYPE.PETROL}
            number={2}
            title="Assign Asset & Agent"
            selected={createSessionStep > 2}
          />

          {+localMotorTypeId !== MOTOR_TYPE.PETROL && (
            <Step
              number={3}
              title="Associate Batteries"
              selected={createSessionStep > 3}
            />
          )}
        </div>

        <button type="button" onClick={() => setEndProcessModal(true)}>
          <CloseIcon className="w-6 h-6 stroke-dark-gray" />
        </button>
      </div>

      <StepDescription
        handleCancel={handleCancel}
        handleCreate={handleSubmit(handleCreate)}
        motorTypeId={localMotorTypeId}
      />

      {/* body of the step */}
      {renderSteps()}

      <EndProcessModal
        visible={endProcessModal}
        onCancel={() => setEndProcessModal(false)}
      />

      <ToastMessageModal
        visible={showToastMessage?.visible}
        onCancel={() =>
          dispatch(
            updateAsset({
              key: "showToastMessage",
              value: {
                visible: false,
                data: {},
              },
            }),
          )
        }
        title={showToastMessage?.data?.title}
        description={showToastMessage?.data?.description}
        isError={showToastMessage?.data?.isError}
      />
    </CustomLayout>
  );
};

export default CreateSessionScreen;
